import type { SelectProps } from 'aui';
import { Radio, Select } from 'aui';
import clsx from 'clsx';
import React from 'react';
import styles from './SelectRadio.module.css';

export type SelectRadioProps = {
  prefixNode?: React.ReactNode;
  className?: string;
  options: SelectProps['options'];
  value?: string;
  onChange?: (value: string) => void;
  type: 'radio' | 'select';
  minShowItemCount?: number;
  theme?: 'light' | 'dark';
  customCalcLength?: (options: SelectProps['options']) => number;
  popupClassName?: string;
};

export function SelectRadio(props: SelectRadioProps) {
  const {
    className,
    type,
    prefixNode,
    value,
    onChange,
    options = [],
    customCalcLength = (v) => v?.length ?? 0,
    minShowItemCount = 0,
    popupClassName,
    theme = 'light',
  } = props;

  const length = customCalcLength(options);

  const isShow = React.useMemo(() => {
    return length > minShowItemCount;
  }, [length, minShowItemCount]);

  const hasPrefixNode = !(prefixNode === false || prefixNode === null || prefixNode === undefined);
  if (!isShow && !hasPrefixNode) {
    return null;
  }
  return (
    <div className={clsx('', className)}>
      {type === 'select' && (
        <>
          {prefixNode}
          {isShow && (
            <Select
              dropdownMatchSelectWidth={false}
              bordered={false}
              className={styles.select}
              popupClassName={clsx(styles['select-popup'], popupClassName)}
              value={value}
              options={options}
              size="small"
              onChange={(v) => {
                onChange?.(v);
              }}
            />
          )}
        </>
      )}

      {type === 'radio' && (
        <>
          {isShow && (
            <Radio.Group
              value={value}
              buttonStyle="soft-secondary-separated"
              size="small"
              className={clsx('flex-wrap gap-1', {
                [`${styles['radio-group-dark']}`]: theme === 'dark',
                [`${styles['radio-group-light']}`]: theme === 'light',
              })}
            >
              {prefixNode}

              {options.map((x) => {
                return (
                  <Radio.Button
                    key={x.value}
                    value={x.value}
                    onChange={() => {
                      onChange?.(String(x.value));
                    }}
                  >
                    {x.label}
                  </Radio.Button>
                );
              })}
            </Radio.Group>
          )}

          {!isShow && prefixNode}
        </>
      )}
    </div>
  );
}
